import { Locale } from '../types/localization'

type TranslationValue = string | { [key: string]: TranslationValue }
type LocaleTranslations = Record<string, TranslationValue>
type ResourcesType = Partial<Record<Locale, LocaleTranslations>>
type FallbackResourcesType = Partial<Record<Locale | 'default', Array<Locale>>>
type LocaleLoader = () => Promise<{ default: LocaleTranslations }>

/**
 * Per-locale dynamic import. Bundlers (Rollup/Vite/webpack) emit each JSON as
 * its own chunk, so only the requested locale (plus its fallback chain) is
 * fetched at runtime.
 */
const loaders: Partial<Record<Locale, LocaleLoader>> = {
  [Locale.daDK]: () => import('./texts/da-dk.json'),
  [Locale.deDE]: () => import('./texts/de-de.json'),
  [Locale.enGB]: () => import('./texts/en-gb.json'),
  [Locale.enIE]: () => import('./texts/en-ie.json'),
  [Locale.enUS]: () => import('./texts/en-us.json'),
  [Locale.esES]: () => import('./texts/es-es.json'),
  [Locale.esUS]: () => import('./texts/es-us.json'),
  [Locale.fiFI]: () => import('./texts/fi-fi.json'),
  [Locale.frCA]: () => import('./texts/fr-ca.json'),
  [Locale.frFR]: () => import('./texts/fr-fr.json'),
  [Locale.itIT]: () => import('./texts/it-it.json'),
  [Locale.nbNO]: () => import('./texts/nb-no.json'),
  [Locale.nlNL]: () => import('./texts/nl-nl.json'),
  [Locale.ptPT]: () => import('./texts/pt-pt.json'),
  [Locale.svSE]: () => import('./texts/sv-se.json'),
}

export const supportedLocales = Object.keys(loaders) as Locale[]

/**
 * Translations that have been loaded so far. Populated lazily by `loadLocale`.
 * Exposed for backwards compatibility with consumers that read it directly
 * and for tests.
 */
export const resources: ResourcesType = {}

export const fallbackLng: FallbackResourcesType = {
  [Locale.frBE]: [Locale.frFR, Locale.enIE],
  [Locale.frCA]: [Locale.frFR, Locale.enIE],
  [Locale.nlBE]: [Locale.nlNL, Locale.enIE],
  [Locale.deAT]: [Locale.deDE, Locale.enIE],
  [Locale.deCH]: [Locale.deDE, Locale.enIE],
  [Locale.itCH]: [Locale.itIT, Locale.enIE],
  [Locale.frCH]: [Locale.frFR, Locale.enIE],
  [Locale.enAU]: [Locale.enIE],
  [Locale.enCA]: [Locale.enIE],
  [Locale.enGB]: [Locale.enIE],
  [Locale.enUS]: [Locale.enIE],
  [Locale.esUS]: [Locale.enIE],
  [Locale.enIN]: [Locale.enIE],
  [Locale.enNZ]: [Locale.enAU, Locale.enIE],
  [Locale.enSG]: [Locale.enAU, Locale.enIE],
  default: [Locale.enIE],
}

const inFlight: Partial<Record<Locale, Promise<LocaleTranslations | undefined>>> = {}

/**
 * Clears all cached locale data (resources and in-flight promises).
 * Intended for tests only — allows each test to start with a clean slate.
 */
export const clearLocaleCache = (): void => {
  for (const key of Object.keys(resources)) {
    delete resources[key as Locale]
  }
  for (const key of Object.keys(inFlight)) {
    delete inFlight[key as Locale]
  }
  for (const key of Object.keys(I18n.instances)) {
    delete I18n.instances[key as Locale]
  }
}

const isSupported = (locale: Locale): boolean => Object.prototype.hasOwnProperty.call(loaders, locale)

const resolveFallbackChain = (locale: Locale): Locale[] => {
  const chain = fallbackLng[locale] ?? fallbackLng.default ?? []
  return chain.filter(isSupported)
}

/**
 * Reports a locale chunk fetch failure so we can detect per-locale CDN issues
 * in New Relic / structured logs without coupling this module to any logger
 * implementation (avoids circular imports and keeps the loader usable in
 * tests and on the SSR node target).
 */
const reportLocaleLoadFailure = (locale: Locale, error: unknown): void => {
  const message = '[bookends-v2] Failed to load locale chunk'
  const context = {
    locale,
    error: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
  }
  try {
    if (typeof window !== 'undefined') {
      // Lazy-required so the browser logger is only pulled in client-side and
      // a failure inside the logger itself can't break locale loading.
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { Logger } = require('../utils/logger') as typeof import('../utils/logger')
      Logger.instance.error(message, context)
      return
    }
  } catch {
    // fall through to console
  }
  console.error(message, context)
}

/**
 * Loads a single locale's JSON. Idempotent; results are cached in `resources`.
 */
const loadSingleLocale = (locale: Locale): Promise<LocaleTranslations | undefined> => {
  if (resources[locale]) {
    return Promise.resolve(resources[locale])
  }
  if (inFlight[locale]) {
    return inFlight[locale]!
  }
  if (!isSupported(locale)) {
    return Promise.resolve(undefined)
  }

  const promise = loaders[locale]!()
    .then((mod) => {
      const data = (mod?.default ?? (mod as unknown)) as LocaleTranslations
      resources[locale] = data
      return data
    })
    .catch((err) => {
      // Failed loads should not be permanently cached, so callers can retry.
      delete inFlight[locale]
      reportLocaleLoadFailure(locale, err)
      throw err
    })

  inFlight[locale] = promise
  return promise
}

/**
 * Loads a single locale file – the requested locale if supported, otherwise
 * the first available fallback. Only one locale file is ever loaded.
 */
export const loadLocale = async (locale: Locale): Promise<void> => {
  if (isSupported(locale)) {
    await loadSingleLocale(locale)
    return
  }

  const firstFallback = resolveFallbackChain(locale)[0]
  if (firstFallback) {
    await loadSingleLocale(firstFallback)
  }
}

/**
 * Eagerly loads every supported locale. Intended for tests/tooling only.
 */
export const loadAllLocales = async (): Promise<void> => {
  await Promise.all(supportedLocales.map(loadSingleLocale))
}

class I18n {
  locale: Locale = Locale.esUS

  private constructor(locale: Locale) {
    const isKnown = isSupported(locale) || Object.prototype.hasOwnProperty.call(fallbackLng, locale)
    this.locale = isKnown ? locale : ((fallbackLng?.default?.[0] as Locale) ?? Locale.enIE)
  }

  static readonly instances: Record<Locale, I18n> = {} as Record<Locale, I18n>

  static getInstance(locale = Locale.enIE): I18n {
    if (locale && I18n.instances[locale]) {
      return I18n.instances[locale]
    }

    const instance = new I18n(locale)
    I18n.instances[locale] = instance
    return instance
  }

  setLocale(locale: Locale): I18n {
    return I18n.getInstance(locale)
  }

  translate(key: string) {
    const translation = this.getTranslation(this.locale, key)
    if (translation !== null) {
      return translation
    }
    return this.getFallbackTranslation(key) || key
  }

  getTranslation(locale: Locale, key: string): string | null {
    const translations = resources?.[locale]

    // add support for nested keys using dot notation, e.g. "header.title"
    const keyParts = key.split('.')
    let currentTranslations: TranslationValue | undefined = translations as TranslationValue | undefined
    for (const part of keyParts) {
      if (currentTranslations && typeof currentTranslations === 'object' && part in currentTranslations) {
        currentTranslations = (currentTranslations as Record<string, TranslationValue>)[part]
      } else {
        return null
      }
    }

    if (typeof currentTranslations === 'string') {
      return currentTranslations
    }

    return null
  }

  getAllTexts() {
    if (resources[this.locale]) {
      return resources[this.locale]
    }
    const fallbacks = fallbackLng[this.locale] || fallbackLng.default
    if (fallbacks) {
      for (const fallback of fallbacks) {
        const translations = resources[fallback]
        if (translations !== null) {
          return translations
        }
      }
    }
    return undefined
  }

  getFallbackTranslation(key: string) {
    const fallbacks = fallbackLng[this.locale] || fallbackLng.default
    if (fallbacks) {
      for (const fallback of fallbacks) {
        const translation = this.getTranslation(fallback, key)
        if (translation !== null) {
          return translation
        }
      }
    }
    return null
  }
}

/**
 * Synchronous accessor. Returns an I18n instance immediately; callers must
 * have called `loadLocale(locale)` (or `getI18nInstanceAsync`) beforehand to
 * guarantee translations are available.
 */
export const getI18nInstance = (locale = Locale.enIE) => {
  locale = (locale as string).toLowerCase() as Locale
  return I18n.getInstance(locale)
}

/**
 * Loads the locale (and its fallback chain) on demand, then returns the
 * ready-to-use I18n instance. Preferred entry point for runtime callers.
 */
export const getI18nInstanceAsync = async (locale = Locale.enIE): Promise<I18n> => {
  const normalized = (locale as string).toLowerCase() as Locale
  await loadLocale(normalized)
  return I18n.getInstance(normalized)
}
